import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, type Resolver } from "react-hook-form";
import toast from "react-hot-toast";
import {
  Check,
  CircleAlert,
  CircleCheck,
  ChevronLeft,
  ChevronRight,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  useGetCurrentLocalFacilityId,
  useFacilitiesByUserId,
} from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import {
  useCreateLeagueMutation,
  useFinalizeLeagueMutation,
  useUpdateLeagueMutation,
} from "../../api/endpoints/leagues";
import {
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  RosterTeeType,
  TeeType,
} from "../../enums";
import {
  advancedLeagueSchema,
  isTeamBasedFormat,
  SCORING_DEFAULTS,
  type AdvancedLeagueFormValues,
} from "./advancedLeagueSchema";
import { useGetLeagueQuery } from "../../api/endpoints/leagueDetail";
import { useGetLeagueRosterQuery } from "../../api/endpoints/roster";
import { useGetLeagueTeamsQuery } from "../../api/endpoints/teams";
import { useGetLeagueRoundsQuery } from "../../api/endpoints/rounds";
import {
  DEFAULT_TEAM_SIZE,
  fullTimePlayers,
  isSupportedTeamSize,
  type BoardId,
} from "../teams/teamsBoardModel";
import { useLeagueFacilityId } from "../../hooks/useLeagueFacilityId";
import { anyLeagueRoundStarted } from "../teams/teamEditPolicy";
import {
  toCreateLeagueArgs,
  toUpdateLeagueBody,
  fromLeagueDetail,
} from "./advancedLeagueMapping";
import { StructureTab } from "./tabs/StructureTab";
import { ScheduleDatesTab } from "./tabs/ScheduleDatesTab";
import { LocationTab } from "./tabs/LocationTab";
import { RosterTab } from "./tabs/RosterTab";
import { TeamsTab, type TeamsTabHandle } from "./tabs/TeamsTab";
import { ReviewTab } from "./tabs/ReviewTab";
import { deriveLeagueStepIssues, type LeagueStepIssueKey } from "./stepIssues";
import { PlaceholderTab } from "./tabs/PlaceholderTab";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { LeagueCreatedDialog } from "../common/LeagueCreatedDialog";
import { MatchPlayFinalizeDialog } from "../common/MatchPlayFinalizeDialog";
import {
  extractServerErrorLines,
  leagueNameConflictMessage,
  isMissingParticipatingFacilities,
  participatingIdsToSend,
  toRosterTeeType,
} from "../helpers";
import type { Clubhouse_LeagueRoundFinalizeBlockingError } from "../../types/leagueRound";
import { LeaguesV2Events } from "../../analytics/leaguesV2Events";
import { useTrackLeaguesV2View } from "../../analytics/useTrackLeaguesV2View";

function schedulingTimezoneOf(
  leagueTimezone?: string,
  facilityTimezone?: string,
): string | undefined {
  return leagueTimezone || facilityTimezone || undefined;
}

/** Pull a server-provided message out of an RTK Query error, else the fallback. */
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (typeof data === "string" && data) return data;
    if (data && typeof data === "object" && "message" in data) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
  }
  return fallback;
}

type TabKey =
  | "structure"
  | "schedule"
  | "location"
  | "roster"
  | "teams"
  | "review";

// v4 create-wizard step order. Steps not visible for this league drop out:
// Location for single-facility accounts, Teams for non-team formats — the
// stepper is built by intersecting this with the visible tabs.
const STEP_ORDER: TabKey[] = [
  "structure",
  "schedule",
  "location",
  "roster",
  "teams",
  "review",
];

interface AdvancedLeagueEditorBaseProps {
  initialName?: string;
  initialDescription?: string;
  hasMultipleFacilities?: boolean;
  defaultValues?: Partial<AdvancedLeagueFormValues>;
  onExit?: () => void;
  onGoToDashboard?: () => void;
  onOpenLeague?: (leagueId: string) => void;
}

/** Create has no leagueId (one is minted on save); edit requires one. Modeling
 *  this as a union makes "edit without a leagueId" unrepresentable. */
export type AdvancedLeagueEditorProps = AdvancedLeagueEditorBaseProps &
  ({ mode: "create"; leagueId?: never } | { mode: "edit"; leagueId: string });

export function AdvancedLeagueEditor({
  mode,
  leagueId: editLeagueId,
  initialName = "",
  initialDescription = "",
  hasMultipleFacilities = false,
  defaultValues,
  onExit,
  onGoToDashboard,
  onOpenLeague,
}: AdvancedLeagueEditorProps) {
  const facilityId = useGetCurrentLocalFacilityId();
  const [userFacilities, facilitiesQueryState] =
    useFacilitiesByUserId(getUserIdFromToken());
  const [createLeague, { isLoading: creating }] = useCreateLeagueMutation();
  const [finalizeLeague, { isLoading: finalizing }] =
    useFinalizeLeagueMutation();
  const [updateLeague, { isLoading: updating }] = useUpdateLeagueMutation();

  const [createdLeagueId, setCreatedLeagueId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("structure");
  useTrackLeaguesV2View(LeaguesV2Events.VIEW_LEAGUE_EDITOR_STEP, {
    mode,
    step: activeTab,
  });
  const teamsPersistRef = useRef<TeamsTabHandle>(null);
  const [teamsBuilding, setTeamsBuilding] = useState(false);
  const saveTeams = async () => {
    const persist = teamsPersistRef.current?.persistTeams;
    if (!persist) return;
    setTeamsBuilding(true);
    try {
      await persist();
    } finally {
      setTeamsBuilding(false);
    }
  };
  const [error, setError] = useState<string | null>(null);
  // Milestone acknowledgements: the draft-saved crossing and the finalize success.
  const [justSaved, setJustSaved] = useState(false);
  const [roundPlanBuilding, setRoundPlanBuilding] = useState(false);
  const [finalized, setFinalized] = useState(false);
  // Finalizing locks parts of the league, so confirm before doing it.
  const [confirmFinalizeOpen, setConfirmFinalizeOpen] = useState(false);

  const justSavedTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      if (justSavedTimer.current) window.clearTimeout(justSavedTimer.current);
    },
    [],
  );

  /** Visible ack after Save draft — PATCH when dirty, or confirm already saved
   *  (Roster/Review, where roster writes land immediately). */
  const acknowledgeDraftSaved = (toastMessage = "Draft saved") => {
    toast.success(toastMessage, {
      position: "top-center",
      className: "text-sm poppins",
    });
    setJustSaved(true);
    if (justSavedTimer.current) window.clearTimeout(justSavedTimer.current);
    justSavedTimer.current = window.setTimeout(() => setJustSaved(false), 2500);
  };

  const { settings: defaultSettings, ...restDefaults } = defaultValues ?? {};
  const form = useForm<AdvancedLeagueFormValues>({
    resolver: zodResolver(
      advancedLeagueSchema,
    ) as Resolver<AdvancedLeagueFormValues>,
    mode: "onBlur",
    defaultValues: {
      name: initialName,
      description: initialDescription,
      facilityId,
      // Create starts with no tee chosen — the operator must pick one (no
      // silent default to click through). Edit still falls back to White/Red
      // as a display placeholder for the brief window before hydration
      // replaces it with the league's real saved tees.
      settings: {
        ...(mode === "create"
          ? {}
          : { mensTee: TeeType.WHITE, womensTee: TeeType.RED }),
        ...defaultSettings,
      },
      // Per-format scoring defaults for a brand-new league (point values, match
      // points, side contests). In edit mode we don't seed here — the
      // reset() below hydrates scoringPolicy from the league detail, so an
      // untouched field stays out of the dirty set and is never PATCHed over.
      //
      // matchScoringMode is the one SCORING_DEFAULTS field intentionally left
      // unset here: the point VALUES are an allowed pre-filled default, but
      // which match type to use is a real choice the operator must make, so it
      // starts unanswered like League Format / Scoring Format.
      ...(mode === "create"
        ? {
            scoringPolicy: {
              ...SCORING_DEFAULTS,
              matchPlay: {
                ...SCORING_DEFAULTS.matchPlay,
                matchScoringMode: undefined,
              },
            },
          }
        : {}),
      seasonRounds: [],
      ...restDefaults,
    } as AdvancedLeagueFormValues,
  });

  const leagueId = mode === "edit" ? (editLeagueId ?? null) : createdLeagueId;
  const phase: "pre-draft" | "draft" =
    mode === "edit" || leagueId ? "draft" : "pre-draft";

  // Edit mode: load the existing league and hydrate the form once.
  const { data: editLeagueData } = useGetLeagueQuery(editLeagueId ?? "", {
    skip: mode !== "edit" || !editLeagueId,
  });

  const hydratedRef = useRef(false);

  const leagueType = form.watch("settings.leagueType");
  // Live Season Setup tees, so Add Player uses them before the draft is saved.
  const leagueMensTee =
    toRosterTeeType(form.watch("settings.mensTee")) ?? RosterTeeType.WHITE;
  const leagueWomensTee =
    toRosterTeeType(form.watch("settings.womensTee")) ?? RosterTeeType.RED;
  // Gate "Save changes" on the form being dirty. Destructured here so the RHF
  // formState proxy subscribes and re-renders as fields change.
  // dirtyFields must be read during render. The formState proxy returns an
  // empty map from an event handler when this field was never subscribed, so a
  // tee change looked dirty to the button and then vanished from the PATCH.
  const { isDirty, dirtyFields } = form.formState;

  // Season Setup's generated rounds live in the form until Save draft sends
  // them, so leaving with unsaved work silently discards the whole plan.
  const { isBlocked, confirmLeave, cancelLeave } =
    useUnsavedChangesGuard(isDirty);

  // Live per-step completion drives the numbered tab circles: a green check once
  // the step's required fields are filled, otherwise the step number.
  const leagueFacilityId = useLeagueFacilityId(leagueId ?? undefined);
  // League-scoped mutations (PATCH, finalize) must target the league's OWN
  // facility, not the sidebar-selected one — otherwise the API 400s with
  // "No league with the given id was found at <facility>". Falls back to the
  // current facility (correct for a just-created draft whose detail hasn't
  // resolved yet).
  const leagueApiFacilityId = leagueFacilityId ?? facilityId;
  const leagueSchedulingTimezone =
    editLeagueData?.leagueV2Detail?.season?.schedulingTimezone ??
    editLeagueData?.metaData?.schedulingTimezone;
  const facilitySchedulingTimezone = userFacilities.find(
    (f) => f.id === leagueApiFacilityId,
  )?.timeZoneIana;
  const schedulingTimezone = schedulingTimezoneOf(
    leagueSchedulingTimezone,
    facilitySchedulingTimezone,
  );
  // Decode and PATCH with the same zone. Wait for the facility list when the
  // league DTO has no snapshot, and in edit also wait for the league's own
  // facility id so we do not hydrate with the sidebar IANA and later PATCH
  // with a different facility's zone.
  const schedulingTimezoneReady = Boolean(
    leagueSchedulingTimezone ||
    (facilitiesQueryState?.isFinished && (mode !== "edit" || leagueFacilityId)),
  );
  useEffect(() => {
    if (
      mode === "edit" &&
      !hydratedRef.current &&
      editLeagueData?.leagueV2Detail &&
      schedulingTimezoneReady
    ) {
      hydratedRef.current = true;
      form.reset({
        facilityId,
        seasonRounds: [],
        seasonRoundCount: 0,
        ...fromLeagueDetail(editLeagueData, schedulingTimezone),
      });
    }
  }, [
    mode,
    editLeagueData,
    form,
    facilityId,
    schedulingTimezone,
    schedulingTimezoneReady,
  ]);
  // Whether Match Play has begun, which is what locks the full-time roster. Only
  // an existing league can have started, so this is skipped while creating one.
  const { data: editLeagueRounds } = useGetLeagueRoundsQuery(
    { facilityId: leagueFacilityId ?? "", leagueId: leagueId ?? "" },
    { skip: !leagueFacilityId || !leagueId },
  );
  const { data: rosterData } = useGetLeagueRosterQuery(
    { facilityId: leagueFacilityId ?? "", leagueId: leagueId ?? "" },
    { skip: !leagueFacilityId || !leagueId },
  );
  const rosterMembers = (rosterData ?? []).filter((m) => m.isActive ?? true);
  // Roster is "complete" once it has anyone on it. A missing starting handicap
  // no longer holds a member back — the backend establishes one from the rounds
  // they post — so the tab must not keep withholding its tick for a member the
  // table itself now shows as Ready.
  const rosterReady = rosterMembers.length >= 1;

  // Individual leagues have no Teams tab, so don't fetch teams for them.
  const isTeamLeague = isTeamBasedFormat(leagueType);
  const { data: teamsData } = useGetLeagueTeamsQuery(
    { facilityId: leagueFacilityId ?? "", leagueId: leagueId ?? "" },
    { skip: !leagueFacilityId || !leagueId || !isTeamLeague },
  );
  // A team step is complete only when the full-time roster divides into full
  // teams. An odd count always leaves one open slot, so the step stays open
  // until the roster itself changes. Absence of a reported size is the
  // product's pair size, the same rule the board uses.
  const reportedTeamSize = editLeagueData?.leagueV2Detail?.teamSize;
  const teamSize = isSupportedTeamSize(reportedTeamSize)
    ? reportedTeamSize
    : DEFAULT_TEAM_SIZE;
  const teamsReady = useMemo(() => {
    const players = fullTimePlayers(rosterData);
    if (players.length === 0 || players.length % teamSize !== 0) return false;
    const teamRows = teamsData?.teams ?? [];
    if (teamRows.length !== Math.ceil(players.length / teamSize)) return false;
    // Set<BoardId>, and no Number() on either side: the DTO types userId as a
    // number but the API returns opaque ids like "Qd9bRzV5", which coerce to
    // NaN and match nothing — a fully assigned league would never check off.
    const assigned = new Set<BoardId>(
      teamRows.flatMap((t) =>
        (t.players ?? []).flatMap((p) =>
          p.userId == null ? [] : [p.userId as BoardId],
        ),
      ),
    );
    if (!players.every((p) => assigned.has(p.userId))) return false;
    return teamRows.every((t) => (t.players?.length ?? 0) >= teamSize);
  }, [rosterData, teamsData, teamSize]);

  // Watch only the fields completion depends on (not the whole form) so typing
  // in unrelated inputs doesn't re-render the editor.
  const [wScoring, wStart, wEnd, wMode, wRounds, wRoundCount, wFacilities] =
    form.watch([
      "settings.leagueScoringType",
      "leagueStartDate",
      "leagueEndDate",
      "scheduleMode",
      "seasonRounds",
      "seasonRoundCount",
      "participatingClubhouseFacilityIds",
    ]);
  // Finalizing a match play league locks the roster (and its teams) for good,
  // so those two formats get their own warning instead of the generic confirm.
  // Scramble is a team format but not a match play one, hence the explicit list.
  const isMatchPlayLeague =
    wScoring === Clubhouse_LeagueScoring.MATCH &&
    (leagueType === Clubhouse_LeagueType.INDIVIDUAL ||
      leagueType === Clubhouse_LeagueType.TEAM);
  const isTeamMatchPlay =
    isMatchPlayLeague && leagueType === Clubhouse_LeagueType.TEAM;

  // Problems worth colouring red, as opposed to steps simply not reached yet.
  const stepIssues = useMemo(
    () =>
      deriveLeagueStepIssues({
        leagueType,
        teamSize,
        roster: rosterData,
        teams: teamsData?.teams,
      }),
    [leagueType, teamSize, rosterData, teamsData],
  );

  const tabComplete: Record<TabKey, boolean> = {
    structure: leagueType != null && wScoring != null,
    schedule:
      !!wStart &&
      !!wEnd &&
      wMode != null &&
      // Edit hydrates seasonRoundCount (not seasonRounds), so accept either.
      ((wRounds?.length ?? 0) > 0 || (wRoundCount ?? 0) > 0),
    location: (wFacilities?.length ?? 0) >= 1,
    roster: rosterReady,
    teams: teamsReady,
    review: false,
  };

  const tabs = useMemo(() => {
    const all: {
      key: TabKey;
      label: string;
      requiresLeague: boolean;
      visible: boolean;
    }[] = [
      {
        key: "structure",
        label: "Format",
        requiresLeague: false,
        visible: true,
      },
      {
        key: "schedule",
        label: "Season Setup",
        requiresLeague: false,
        visible: true,
      },
      {
        key: "location",
        label: "Location",
        requiresLeague: false,
        visible: hasMultipleFacilities,
      },
      { key: "roster", label: "Roster", requiresLeague: true, visible: true },
      {
        key: "teams",
        label: "Teams",
        requiresLeague: true,
        // Team and Scramble leagues both organize players into teams.
        visible: isTeamBasedFormat(leagueType),
      },
      {
        key: "review",
        label: "Review",
        requiresLeague: true,
        // Always in the tablist — disabled until a draft exists (pre-draft),
        // then available in both create and edit.
        visible: true,
      },
    ];
    return all.filter((t) => t.visible);
  }, [hasMultipleFacilities, leagueType, mode]);

  const stepperTabs = useMemo(
    () =>
      STEP_ORDER.map((key) => tabs.find((t) => t.key === key)).filter(
        (t): t is (typeof tabs)[number] => Boolean(t),
      ),
    [tabs],
  );
  const stepIndex = stepperTabs.findIndex((t) => t.key === activeTab);
  const onStepper = stepIndex !== -1;
  const prevStep = stepIndex > 0 ? stepperTabs[stepIndex - 1] : undefined;
  const nextStep = stepIndex !== -1 ? stepperTabs[stepIndex + 1] : undefined;
  const isReviewStep = activeTab === "review";
  // Format has no date inputs, so a draft can't be created from it. Hide Save
  // draft there until the draft exists (after which it just PATCHes edits).
  // A duplicate-name rejection lands the operator back on Format, and Save
  // draft stays — including after they edit the name — so they can retry.
  const [recoveringName, setRecoveringName] = useState(false);
  const showSaveDraft =
    !(activeTab === "structure" && !leagueId) || recoveringName;

  const applyNameConflict = (err: unknown): boolean => {
    const message = leagueNameConflictMessage(err);
    if (!message) return false;
    form.setError("name", { type: "server", message });
    setRecoveringName(true);
    setActiveTab("structure");
    setError(null);
    return true;
  };

  const handleSaveDraft = async (): Promise<boolean> => {
    if (roundPlanBuilding) return false;
    setError(null);
    // Minimum user-entered fields required to create the draft. facilityId is
    // also schema-required but comes from the facility hook, so it's omitted
    // here. Deliberately NOT included: settings.mensTee/womensTee,
    // allowAsynchronousMatchPlay, matchScoringMode — a draft can legitimately
    // be saved before the operator reaches those rows (Season Setup is filled
    // in progressively); they're enforced instead at Finalize, the point the
    // league actually goes live. See handleFinalize.
    const ok = await form.trigger([
      "name",
      "leagueStartDate",
      "leagueEndDate",
      "settings.leagueType",
    ]);
    if (!ok) {
      // Required fields live on different tabs. Route to the first one with an
      // error; the inline FieldError there points at what's missing, rather
      // than a red banner scolding the operator.
      const errs = form.formState.errors;
      if (errs.name || errs.settings?.leagueType) {
        setActiveTab("structure");
      } else if (errs.leagueStartDate || errs.leagueEndDate) {
        setActiveTab("schedule");
      }
      return false;
    }
    // Build the payload outside the try so a mapping bug surfaces as itself,
    // not mislabeled as a save failure.
    const values = form.getValues();
    // The Location tab stores the exact facilities the operator chose; send them
    // (a single non-home facility included) rather than every facility they own.
    const participating = participatingIdsToSend(
      values.participatingClubhouseFacilityIds,
    );
    // Wait for the facility list before create so schedulingTimezone is the
    // facility IANA (not a browser fallback) once timeZoneIana has loaded.
    // The participating guard only covers multi-facility scope.
    if (
      !schedulingTimezoneReady ||
      isMissingParticipatingFacilities(
        values.facilityScope,
        participating,
        !!facilitiesQueryState?.isFinished,
      )
    ) {
      setError(
        "Your facilities are still loading. Please try again in a moment.",
      );
      return false;
    }
    const args = toCreateLeagueArgs(values, schedulingTimezone, participating);
    try {
      const result = await createLeague(args).unwrap();
      if (result?.id == null) {
        console.error("createLeague returned no id", result);
        setError("The league could not be created. Please try again.");
        return false;
      }
      setCreatedLeagueId(String(result.id));
      // Reset the dirty baseline, exactly as the PATCH path does. Without it
      // `isDirty` stays true for the rest of the session with nothing actually
      // outstanding — which re-disables nothing visibly, but does make the
      // unsaved-changes guard claim the league was never saved on every
      // subsequent navigation.
      form.reset(form.getValues());
      // Acknowledge the pre-draft -> draft crossing; auto-dismisses.
      setJustSaved(true);
      justSavedTimer.current = window.setTimeout(
        () => setJustSaved(false),
        2500,
      );
      return true;
    } catch (err) {
      console.error("createLeague failed", err);
      if (applyNameConflict(err)) return false;
      // Surface the server's actual validation text so the operator knows what
      // to fix; only fall back to generic copy if the response carries nothing.
      const lines = extractServerErrorLines(err);
      setError(
        lines.length > 0
          ? lines.join("\n")
          : apiErrorMessage(
              err,
              "The draft couldn't be saved. Please try again.",
            ),
      );
      return false;
    }
  };

  /** Persist dirty editor fields. Returns false when the PATCH fails so
   *  callers (Save changes, Finalize) can stop. */
  const persistDirtyChanges = async (options?: {
    silent?: boolean;
    toastMessage?: string;
  }): Promise<boolean> => {
    if (!leagueId) return false;
    if (!isDirty) return true;
    setError(null);
    // Send only fields the operator actually changed, so untouched values the
    // detail DTO couldn't hydrate (handicap, substitutes) aren't clobbered.
    const values = form.getValues();
    // The Location tab stores the exact facilities the operator chose; send them
    // (a single non-home facility included) rather than every facility they own.
    const participating = participatingIdsToSend(
      values.participatingClubhouseFacilityIds,
    );
    // Facility fields are sent when the scope or the participating list changed;
    // if either did, don't PATCH a multi-facility scope with nowhere to play.
    const facilityDirty =
      !!form.formState.dirtyFields.facilityScope ||
      !!form.formState.dirtyFields.participatingClubhouseFacilityIds;
    if (
      !schedulingTimezoneReady ||
      (facilityDirty &&
        isMissingParticipatingFacilities(
          values.facilityScope,
          participating,
          !!facilitiesQueryState?.isFinished,
        ))
    ) {
      setError(
        "Your facilities are still loading. Please try again in a moment.",
      );
      return false;
    }
    const body = toUpdateLeagueBody(
      values,
      schedulingTimezone,
      dirtyFields,
      participating,
    );
    try {
      await updateLeague({
        facilityId: leagueApiFacilityId,
        leagueId,
        body,
      }).unwrap();
      // Reset the dirty baseline to the just-saved values so the button
      // re-disables, and dirtyFields is correct for the next save.
      form.reset(form.getValues());
      if (!options?.silent) {
        toast.success(options?.toastMessage ?? "Changes saved", {
          position: "top-center",
          className: "text-sm poppins",
        });
      }
      return true;
    } catch (err) {
      console.error("updateLeague failed", err);
      if (applyNameConflict(err)) return false;
      const errBody = (
        err as {
          data?: Clubhouse_LeagueRoundFinalizeBlockingError & {
            devMessage?: string;
          };
        }
      )?.data;
      const issues = (
        errBody?.errors ? Object.values(errBody.errors).flat().join("\n") : ""
      )
        .split("\n")
        .map((line: string) => line.trim())
        .filter(Boolean);
      setError(
        issues.length > 0
          ? issues.join("\n")
          : "Failed to save changes. Please try again.",
      );
      return false;
    }
  };

  // Before the draft exists Save draft creates the league; afterward it PATCHes.
  const saveDraftNow = async () => {
    await saveTeams();
    if (leagueId) {
      await persistDirtyChanges({ toastMessage: "Draft saved" });
    } else {
      await handleSaveDraft();
    }
  };

  const handleSaveDraftClick = () => {
    void (async () => {
      await saveTeams();
      if (leagueId) {
        const ok = await persistDirtyChanges({ silent: true });
        if (ok) acknowledgeDraftSaved();
      } else {
        await handleSaveDraft();
      }
    })();
  };

  // Advancing to a league-gated step (Roster) before a draft exists saves the
  // draft first, then moves on — so single-facility flows (no Location buffer
  // step between Season Setup and Roster) aren't dead-ended by a disabled Next.
  const handleNext = async () => {
    if (!nextStep || roundPlanBuilding || teamsBuilding) return;
    if (activeTab === "teams") await saveTeams();
    if (nextStep.requiresLeague && !leagueId) {
      if (await handleSaveDraft()) setActiveTab(nextStep.key);
      return;
    }
    setActiveTab(nextStep.key);
  };

  const handleFinalize = async () => {
    if (!leagueId || roundPlanBuilding) return;
    setError(null);
    // Save Draft deliberately allows these to stay unanswered (Season Setup is
    // filled in progressively), but Finalize is the point the league actually
    // goes live — so this is where an explicit choice (no UI default to fall
    // back on) is actually enforced, not just cosmetically requested.
    const ok = await form.trigger([
      "settings.mensTee",
      "settings.womensTee",
      "settings.allowAsynchronousMatchPlay",
      "scoringPolicy.matchPlay.matchScoringMode",
    ]);
    if (!ok) {
      const errs = form.formState.errors;
      if (
        errs.settings?.allowAsynchronousMatchPlay ||
        errs.scoringPolicy?.matchPlay?.matchScoringMode
      ) {
        setActiveTab("structure");
      } else if (errs.settings?.mensTee || errs.settings?.womensTee) {
        setActiveTab("schedule");
      }
      setError(
        "Finish Season Setup before finalizing — some required settings haven't been chosen yet.",
      );
      return;
    }
    // Operators reasonably expect Finalize to keep what they just set in the
    // UI (e.g. switching TEAM → Individual). Persist first; skip finalize if
    // the save is rejected so we never activate stale structure.
    await saveTeams();
    const saved = await persistDirtyChanges({ silent: true });
    if (!saved) return;
    try {
      await finalizeLeague({
        facilityId: leagueApiFacilityId,
        leagueId,
      }).unwrap();
      setFinalized(true);
    } catch (err) {
      const issues = extractServerErrorLines(err);
      setError(
        issues.length > 0
          ? issues.join("\n")
          : "Failed to finalize the league. Please try again.",
      );
    }
  };

  const saveDraftButton = (
    <Button
      type="button"
      variant="outline"
      onClick={handleSaveDraftClick}
      disabled={creating || updating || roundPlanBuilding || teamsBuilding}
      className="rounded-xl"
    >
      {(creating || updating) && <Spinner data-icon="inline-start" />}
      Save draft
    </Button>
  );

  const finalizeButton = (
    <Button
      type="button"
      variant="secondary"
      className="group/finalize gap-1.5 poppins text-xs font-semibold tracking-tight rounded-xl motion-safe:hover:-translate-y-px motion-safe:hover:shadow-md active:scale-[0.97]"
      onClick={() => setConfirmFinalizeOpen(true)}
      disabled={
        finalizing ||
        updating ||
        finalized ||
        roundPlanBuilding ||
        teamsBuilding
      }
    >
      {finalizing ? (
        <Spinner data-icon="inline-start" />
      ) : finalized ? (
        <CircleCheck
          data-icon="inline-start"
          className="motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-300"
        />
      ) : (
        <Trophy
          data-icon="inline-start"
          className="motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out motion-safe:group-hover/finalize:-rotate-12 motion-safe:group-hover/finalize:scale-110"
        />
      )}
      {finalized ? "Created" : "Finalize League"}
    </Button>
  );
  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-4">
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            void (async () => {
              if (activeTab === "teams" && value !== "teams") await saveTeams();
              setActiveTab(value as TabKey);
            })();
          }}
        >
          {/* A stepper, not a tab bar: no enclosing box and no pill behind the
              current step. Each step is its number and its name, joined to the
              next by a rule, and the one you are on is the one that is filled
              in. The box and the pill were two ways of saying "here", on a
              control whose numbers already say it. */}
          <TabsList
            variant="line"
            className="h-auto w-full justify-start gap-2 overflow-x-auto bg-transparent p-0 group-data-horizontal/tabs:h-auto"
          >
            {tabs.map((t, i) => {
              const issue = stepIssues[t.key as LeagueStepIssueKey];
              // An issue outranks completion: a step can satisfy every field it
              // owns and still hold state the server will refuse.
              const done = !issue && tabComplete[t.key];
              const current = activeTab === t.key;
              return (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  className={cn(
                    // after:hidden drops the line variant's active underline,
                    // which would be a third marker for the step the filled
                    // circle already names.
                    "gap-2 rounded-none px-0 py-0 after:hidden data-active:bg-transparent",
                    // The first step sits on the left edge; every other one
                    // takes an equal share of the row, and the rule joining it
                    // to the step before absorbs that share. So the stepper
                    // spans the same width as the form under it, however many
                    // steps this league's format happens to have.
                    i === 0
                      ? "flex-none"
                      : "flex-1 justify-start before:h-px before:min-w-4 before:flex-1 before:bg-border before:content-['']",
                    current
                      ? "text-text-figure font-semibold"
                      : "text-text-muted font-medium",
                  )}
                  disabled={
                    teamsBuilding || (phase === "pre-draft" && t.requiresLeague)
                  }
                  title={
                    phase === "pre-draft" && t.requiresLeague
                      ? "Available after you save the draft"
                      : issue
                  }
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                      issue
                        ? "bg-destructive text-white"
                        : current
                          ? "bg-primary text-white"
                          : done
                            ? // Done but not current: outlined, so completion
                              // still reads without competing with the step
                              // the operator is actually on.
                              "border border-primary/40 text-primary"
                            : "border border-border text-text-muted",
                    )}
                  >
                    {done && !current ? (
                      <Check className="size-3" strokeWidth={3} />
                    ) : (
                      i + 1
                    )}
                  </span>
                  {t.label}
                  {/* A short marker, not the message: the full sentence names
                      other steps, and folding it into this tab's accessible
                      name would make "Teams" match the Roster tab too. The
                      sentence itself is on the step and in the Review summary. */}
                  {issue && <span className="sr-only">(needs attention)</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="min-h-[260px] pt-4">
            {tabs.map((t) => (
              <TabsContent key={t.key} value={t.key}>
                {t.key === "structure" ? (
                  <StructureTab />
                ) : t.key === "schedule" ? (
                  // Once a draft exists (phase "draft"), its rounds are
                  // backend-owned even in create mode — Season Setup's
                  // client-generated plan was only ever a pre-save stand-in,
                  // so the tab must switch to the real saved rounds the
                  // moment Save draft mints a leagueId, not only when the
                  // editor was opened in true edit mode.
                  <ScheduleDatesTab
                    isEdit={phase === "draft"}
                    leagueId={leagueId}
                    onRoundPlanBuildingChange={setRoundPlanBuilding}
                  />
                ) : t.key === "location" ? (
                  <LocationTab
                    isEdit={mode === "edit"}
                    savedFacilities={editLeagueData?.leagueV2Detail?.facilities}
                  />
                ) : t.key === "roster" && leagueId ? (
                  <RosterTab
                    leagueId={leagueId}
                    leagueMensTee={leagueMensTee}
                    leagueWomensTee={leagueWomensTee}
                    status={editLeagueData?.leagueV2Detail?.status}
                    leagueType={editLeagueData?.leagueV2Detail?.leagueType}
                    scoring={editLeagueData?.leagueV2Detail?.scoring}
                    scheduleMode={
                      editLeagueData?.leagueV2Detail?.season?.scheduleMode
                    }
                    teamSize={editLeagueData?.leagueV2Detail?.teamSize}
                    matchPlayBegun={
                      editLeagueData?.leagueV2Detail?.playBegun === true ||
                      anyLeagueRoundStarted(editLeagueRounds)
                    }
                    rounds={editLeagueRounds}
                  />
                ) : t.key === "teams" && leagueId ? (
                  <TeamsTab
                    ref={teamsPersistRef}
                    leagueId={leagueId}
                    isSaving={teamsBuilding}
                  />
                ) : t.key === "review" && leagueId ? (
                  <ReviewTab
                    leagueId={leagueId}
                    hasMultipleFacilities={hasMultipleFacilities}
                    rosterIssue={stepIssues.roster}
                    onEditStep={setActiveTab}
                  />
                ) : (
                  <PlaceholderTab title={t.label} />
                )}
              </TabsContent>
            ))}
          </div>
        </Tabs>

        <div className="shrink-0 border-t border-border pt-4 flex justify-between items-center">
          {onExit ? (
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={onExit}
            >
              Cancel
            </Button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-3">
            {justSaved && (
              <span
                role="status"
                className="flex items-center gap-1.5 text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-200 motion-reduce:animate-none"
              >
                <CircleCheck className="size-4 text-primary" />
                Draft saved
              </span>
            )}

            {onStepper ? (
              <>
                {prevStep && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setActiveTab(prevStep.key)}
                    disabled={teamsBuilding}
                    className="rounded-xl"
                  >
                    <ChevronLeft data-icon="inline-start" />
                    Back
                  </Button>
                )}
                {showSaveDraft && saveDraftButton}
                {isReviewStep ? (
                  finalizeButton
                ) : (
                  <Button
                    type="button"
                    className="rounded-xl"
                    onClick={() => void handleNext()}
                    disabled={creating || roundPlanBuilding || teamsBuilding}
                  >
                    {teamsBuilding ? (
                      <>
                        <Spinner data-icon="inline-start" />
                        Teams building…
                      </>
                    ) : (
                      <>
                        Next: {nextStep?.label}
                        <ChevronRight
                          data-icon="inline-end"
                          className="motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out motion-safe:group-hover/button:translate-x-0.5"
                        />
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : (
              saveDraftButton
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>Couldn&apos;t complete that action</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <LeagueCreatedDialog
          open={finalized && !!leagueId}
          variant={mode === "edit" ? "finalized" : "created"}
          leagueName={form.getValues("name")}
          onGoToDashboard={() => onGoToDashboard?.() ?? onExit?.()}
          onOpenLeague={() => {
            if (leagueId) onOpenLeague?.(leagueId);
          }}
        />

        <ConfirmDialog
          open={isBlocked}
          onOpenChange={(open) => {
            if (!open) cancelLeave();
          }}
          title="Leave without saving?"
          description="This league hasn't been saved. Any rounds generated here, and every other change on this screen, will be discarded."
          confirmLabel="Discard changes"
          onConfirm={confirmLeave}
        />

        {isMatchPlayLeague ? (
          <MatchPlayFinalizeDialog
            open={confirmFinalizeOpen}
            onOpenChange={setConfirmFinalizeOpen}
            leagueName={form.getValues("name")}
            isTeamLeague={isTeamMatchPlay}
            playerCount={fullTimePlayers(rosterData).length}
            teamCount={teamsData?.teams?.length ?? 0}
            // The window closes either way: a failure is reported by the
            // error alert on the Review step behind it, not in here.
            onSaveDraft={async () => {
              try {
                await saveDraftNow();
              } finally {
                setConfirmFinalizeOpen(false);
              }
            }}
            onFinalize={async () => {
              try {
                await handleFinalize();
              } finally {
                setConfirmFinalizeOpen(false);
              }
            }}
          />
        ) : (
          <ConfirmDialog
            open={confirmFinalizeOpen}
            onOpenChange={setConfirmFinalizeOpen}
            title="Finalize this league?"
            description="Finalizing saves any unsaved changes, then activates the league and locks its setup — structure, scoring and schedule can no longer be edited. This can't be undone."
            confirmLabel="Finalize now"
            isLoading={finalizing || updating}
            onConfirm={() => {
              setConfirmFinalizeOpen(false);
              void handleFinalize();
            }}
          />
        )}
      </div>
    </FormProvider>
  );
}
